import { CommonModule, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit, PLATFORM_ID, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { DateRange, MatDatepickerModule } from '@angular/material/datepicker';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import {
  AuthService,
  BookingService,
  buildListingSlugSegments,
  CurbNTurfCashService,
  FEEDBACK_INCENTIVE_PER_NIGHT,
  IAddOn,
  addOnLineAmount,
  addOnMaxQuantity,
  IListingDetail,
  IMyRv,
  IMyRvProfile,
  IPaymentMethod,
  IPrivateListing,
  IPublicUser,
  ListingAvailabilityService,
  PaymentMethodsService,
  SeoService,
  ToastService,
  computeFeedbackIncentive,
  formatDateRange,
  getActiveRvProfileId,
  hasMyRvPhotos,
  isMyRvComplete,
  isMyRvSet,
  isTowableRv,
  isoKey,
  ListingEntityLookupService,
  listMyRvProfiles,
  myRvMissingFields,
  parseIsoLocal,
  readMyRv,
  rvTypeLabel,
  setActiveRvProfile,
  towVehicleHasData,
  getNights,
  toPrivateListingFromEntity,
  toListingDetailFromEntity,
} from '@cnt-workspace/data-access';
import { IBookingAddOn } from '@cnt-workspace/models';
import { AddOnQtyStepperComponent, FocusTrapDirective, FooterComponent, NavbarComponent } from '@cnt-workspace/ui';
import { CCCvcFormatDirective } from '@curbnturf/cnt-masks/src/lib/cc/directives/cc-cvc-format.directive';
import { CCNumberFormatDirective } from '@curbnturf/cnt-masks/src/lib/cc/directives/cc-number-format.directive';
import { PhoneMaskDirective } from '@curbnturf/cnt-masks/src/lib/phone/phone-mask.directive';
import { ISite } from '@curbnturf/entities';
import { stringFromError } from '@curbnturf/form-helpers/src/lib/helpers/text';
import { gsap } from 'gsap';
import { EMPTY, Subscription } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

// Sections: state — site/RV/add-ons/payment/promo/validation (L73-180) ·
// lifecycle: init, site-id resolve, entry animation (L180-329) · payment card add flow (L329-463) ·
// add-on qty/totals (L463-563) · RV selection, cancellation timeline, field validation (L563-657) ·
// date/guest editors, promo (L657-742) · confirm submission + finalize booking (L742-885)
@Component({
  selector: 'cnt-booking-review',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    MatDatepickerModule,
    MatNativeDateModule,
    NavbarComponent,
    AddOnQtyStepperComponent,
    FooterComponent,
    // todo: add back when we have identity verification
    // IdVerifyModalComponent,
    FocusTrapDirective,
    PhoneMaskDirective,
    CCNumberFormatDirective,
    CCCvcFormatDirective,
  ],
  templateUrl: './booking-review.component.html',
  styleUrls: ['./booking-review.component.scss'],
})
export class BookingReviewComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthService);
  private booking = inject(BookingService);
  private seo = inject(SeoService);
  private toasts = inject(ToastService);
  private payments = inject(PaymentMethodsService);
  private availability = inject(ListingAvailabilityService);
  private listingLookup = inject(ListingEntityLookupService);
  private cash = inject(CurbNTurfCashService);
  private listingSub: Subscription | null = null;
  private cashSub: Subscription | null = null;
  private priceSub: Subscription | null = null;

  listing: IPrivateListing | null = null;
  detail: IListingDetail | null = null;

  /** Real unavailable-date set (blackout + other guests' confirmed reservations),
   *  sourced from ListingAvailabilityService — empty until priceSub's fetch resolves. */
  private unavailableSet = new Set<string>();

  /** SEO slug route back to the listing detail page — booking-review only ever
   *  deals with private (rv-camping) listings, never boondocking. */
  get listingSlugRoute(): string[] {
    return buildListingSlugSegments('rv-camping', this.listing!);
  }

  user: IPublicUser | null = null;
  myRv: IMyRv | null = null;
  /** True while resolving the listing from the live catalog (and, if not supplied via the
   *  `siteId` query param, the real site to book) — gates the initial render so we never
   *  flash a blank/wrong form before identity resolves. */
  loadingListing = true;

  /** The real `ISite.id` this booking will target — from the `siteId` query param
   *  (set by listing-details.component.ts), or resolved directly from the backend
   *  when missing (see `resolveSiteId`, for deep-linked/bookmarked review URLs). Null
   *  for a listing with no real backend site at all (mock/unpublished draft). */
  siteId: number | null = null;

  /** Saved RV profiles + the one this booking is for. */
  rvProfiles: IMyRvProfile[] = [];
  selectedRvId: string | null = null;

  startDate: Date | null = null;
  endDate: Date | null = null;
  guests = 1;
  selectedDateRange: DateRange<Date> | null = null;

  /** Selected add-on IDs, hydrated from `?addOns=...` query param. */
  selectedAddOnIds = new Set<string>();
  /** Quantity per add-on id; only `per unit` rows use values > 1. */
  addOnQuantities = new Map<string, number>();

  /** Reward credit available to spend on this booking. */
  availableCredit = 0;

  /** Form */
  contactEmail = '';
  contactPhone = '';
  noteToHost = '';
  acceptedTerms = false;
  error: string | null = null;
  submitting = false;

  /** Edit-in-place popovers */
  openEditor: 'dates' | 'guests' | null = null;

  /** ID verify modal */
  idVerifyOpen = false;

  /** Saved payment methods — sourced from PaymentMethodsService. */
  selectedPaymentId = '';
  paymentMethods: IPaymentMethod[] = [];
  private paymentsSub: Subscription | null = null;

  /** Inline add-card form (mirrors /account → Payments). */
  addCardOpen = false;
  newCardBrand: IPaymentMethod['brand'] = 'visa';
  newCardLast4 = '';

  /** Real card fields — only rendered when `isRealListing` is true, since
   *  creating a real reservation requires a real Converge-tokenized card
   *  (see PaymentMethodsService.addRealCard). */
  realCardNumber = '';
  realCardExpMonth = '';
  realCardExpYear = '';
  realCardCvv = '';
  realCardAddress = '';
  realCardCity = '';
  realCardState = '';
  realCardZip = '';
  savingRealCard = false;
  realCardError: string | null = null;

  /** Promo code mock */
  promoOpen = false;
  promoCode = '';
  promoApplied: { code: string; amount: number } | null = null;

  readonly feedbackIncentivePerNight = FEEDBACK_INCENTIVE_PER_NIGHT;

  /** Field-level validation errors (live). */
  fieldErrors: { email?: string; phone?: string } = {};
  emailTouched = false;
  phoneTouched = false;

  ngOnInit(): void {
    this.seo.update({
      title: 'Review your booking — CurbNTurf',
      description: 'Review and confirm your CurbNTurf reservation.',
      url: '/booking/review',
      robots: 'noindex, nofollow',
    });

    this.user = this.auth.currentUser;
    this.contactEmail = this.user?.email || '';
    this.contactPhone = this.user?.phone || '';
    this.myRv = readMyRv(this.platformId);
    this.rvProfiles = listMyRvProfiles(this.platformId);
    this.selectedRvId = getActiveRvProfileId(this.platformId);

    const q = this.route.snapshot.queryParamMap;
    const listingId = parseInt(q.get('listingId') || '', 10);
    if (!listingId) {
      this.toasts.info('That property is not bookable here.');
      this.router.navigate(['/search']);
      return;
    }
    const siteIdParam = parseInt(q.get('siteId') || '', 10);
    this.siteId = Number.isFinite(siteIdParam) && siteIdParam > 0 ? siteIdParam : null;

    // Fetches the real listing directly from the backend (ListingEntityLookupService,
    // backed by `GET /listing/:id` against the canonical `listing` table) rather than
    // the search-backed ListingCatalogService — the search index (`search_listing`) only
    // ever carries listings that have actually been indexed for search, so a listing
    // missing from it (e.g. not yet (re)indexed) would otherwise bounce a genuinely
    // bookable listing to /search with a false "not bookable here" toast. Same fetch
    // resolveSiteId() below used to make separately for `sites[]` — now shared.
    let resolved = false;
    this.listingSub = this.listingLookup
      .fetchListingById(listingId)
      .pipe(timeout(15000), catchError(() => EMPTY))
      .subscribe({
        next: (entity) => {
          resolved = true;
          const newListing = toPrivateListingFromEntity(entity);
          if (!newListing) {
            this.loadingListing = false;
            this.toasts.info('That property is not bookable here.');
            this.router.navigate(['/search']);
            return;
          }
          this.listing = newListing;
          const built = toListingDetailFromEntity(entity, newListing);
          this.detail = built.detail;
          // Primes the real post-fee daily-price cache for subtotal()/effectivePricesForRange,
          // and the unavailable-date set for dateFilter/minDate/maxDate — this page can be
          // reached via a bookmarked/deep link without ever visiting /listing/:id, which is
          // what normally triggers this fetch (see ListingAvailabilityService.unavailableSet$).
          this.priceSub = this.availability.unavailableSet$(listingId).subscribe((set) => {
            this.unavailableSet = set;
          });
          this.resolveSiteId(listingId, built.sites, () => {
            this.loadingListing = false;
            this.finishInit(q);
          });
        },
        // A timed-out/errored fetch is funneled through catchError(() => EMPTY), which
        // completes without emitting — if `next` never ran, treat it as not-found (same
        // pattern listing-details.component.ts's resolveListing() uses).
        complete: () => {
          if (!resolved) {
            this.loadingListing = false;
            this.toasts.info('That property is not bookable here.');
            this.router.navigate(['/search']);
          }
        },
      });
  }

  /** Resolves the real `siteId` when the review page was reached without one in the URL
   *  (e.g. a bookmarked/deep-linked `/booking/review` link), from the `sites[]` already
   *  fetched alongside the listing above. Auto-resolves for the (overwhelmingly common)
   *  single-site case; bounces back to the listing page for a multi-site listing so the
   *  guest can pick one with the site-picker there. Leaves `siteId` null — booking falls
   *  back to the publish-snapshot/mock path — when the listing has no real sites at all
   *  (e.g. a host's own unpublished draft). */
  private resolveSiteId(listingId: number, sites: ISite[], done: () => void): void {
    if (this.siteId != null) {
      done();
      return;
    }
    if (sites.length === 1 && sites[0].id) {
      this.siteId = sites[0].id;
    } else if (sites.length > 1) {
      this.toasts.info('Please choose a site for this property before booking.');
      this.router.navigate(['/rv-camping', listingId]);
      return;
    }
    done();
  }

  /** Runs once the listing has resolved from the live catalog — everything here
   *  reads `this.listing`/`this.detail`, set just before this is called. */
  private finishInit(q: ParamMap): void {
    const start = q.get('start');
    const end = q.get('end');
    this.startDate = start ? this.parseIso(start) : null;
    this.endDate = end ? this.parseIso(end) : null;
    if (this.startDate && this.endDate) {
      this.selectedDateRange = new DateRange(this.startDate, this.endDate);
      if (!this.isWithinBookingWindow(this.startDate, this.endDate)) {
        this.error = `Your selected dates are no longer bookable for this listing (check-in on or after ${this.minDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}). Please pick new dates.`;
      }
    }

    const g = parseInt(q.get('guests') || '', 10);
    if (Number.isFinite(g) && g > 0) this.guests = g;

    const addOnsParam = q.get('addOns');
    if (addOnsParam) {
      const ids = new Set<string>();
      const qty = new Map<string, number>();
      for (const tok of addOnsParam.split(',').filter(Boolean)) {
        const [id, qStr] = tok.split(':');
        if (!id) continue;
        ids.add(id);
        const q = parseInt(qStr, 10);
        if (Number.isFinite(q) && q > 0) qty.set(id, q);
      }
      this.selectedAddOnIds = ids;
      this.addOnQuantities = qty;
    }

    if (this.user) {
      if (this.isRealListing && this.user.id) {
        this.cashSub = this.cash.getAvailableCredit(this.user.id).subscribe((v) => (this.availableCredit = v));
      } else {
        this.availableCredit = this.booking.getAvailableCredit(this.user.email);
      }
    }

    this.paymentsSub = this.payments.methods$.subscribe((methods) => {
      this.paymentMethods = methods;
      if (!this.selectedPaymentId || !methods.some((m) => m.id === this.selectedPaymentId)) {
        this.selectedPaymentId = (methods.find((m) => m.isDefault) || methods[0])?.id || '';
      }
    });

    // A real reservation requires a real saved card — pull in whatever's
    // already on file for this account so a returning guest doesn't have to
    // re-enter it every booking.
    if (this.isRealListing && this.user?.id) {
      void this.payments.hydrateRealCard(this.user.id);
    }

    // DOM for the resolved-listing branch hasn't painted yet this tick (listing
    // resolution is now async) — defer the entry animation one macrotask.
    setTimeout(() => this.playEntryAnimation(), 0);
  }

  private playEntryAnimation(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    gsap.from('.review-anim', {
      opacity: 0,
      y: 16,
      duration: 0.55,
      ease: 'power3.out',
      stagger: 0.07,
      delay: 0.05,
      clearProps: 'opacity,transform',
    });
  }

  /** True when a real `ISite.id` was resolved for this booking (via the `siteId` query
   *  param or `resolveSiteId()`) — gates whether booking goes through `POST /reservation`
   *  (and therefore needs a real saved card) vs. the mock localStorage flow. */
  get isRealListing(): boolean {
    return this.siteId != null;
  }

  ngOnDestroy(): void {
    this.paymentsSub?.unsubscribe();
    this.listingSub?.unsubscribe();
    this.cashSub?.unsubscribe();
    this.priceSub?.unsubscribe();
  }

  /** The card the order-summary sidebar's "Charged on ..." line describes. Null once
   *  fake-card seeding is removed and the guest hasn't added a real card yet. */
  get selectedPaymentMethod(): IPaymentMethod | null {
    return this.paymentMethods.find((m) => m.id === this.selectedPaymentId) ?? null;
  }

  /** True once a real (Converge-tokenized) card is on file — this system only
   *  ever keeps one, so the UI hides "add" in favor of replace/remove. */
  get hasRealCard(): boolean {
    return this.paymentMethods.some((m) => m.isReal);
  }

  /** Like hasRealCard, but also requires the card to have passed
   *  hydrateRealCard()'s double-check against the server — this is what
   *  actually gates booking submission. `isActive === undefined` (not
   *  verified yet this page load) counts as provisionally OK so the button
   *  isn't wrongly disabled before that async check resolves; it flips
   *  reactively via methods$ the moment it does. */
  get hasValidRealCard(): boolean {
    return this.paymentMethods.some((m) => m.isReal && m.isActive !== false);
  }

  removeCard(id: string): void {
    this.payments.remove(id);
    this.toasts.info('Card removed.');
  }

  /** Single-click delete + reopen the add form — the real-card form branch
   *  renders immediately since `addCardOpen` flips true and `isRealListing`
   *  is unchanged. */
  replaceCard(id: string): void {
    this.removeCard(id);
    this.addCardOpen = true;
  }

  /** Inline add-card form (mirrors /account → Payments). */
  get canAddCard(): boolean {
    return /^\d{4}$/.test(this.newCardLast4);
  }
  cancelAddCard(): void {
    this.addCardOpen = false;
    this.newCardLast4 = '';
    this.newCardBrand = 'visa';
    this.realCardNumber = '';
    this.realCardExpMonth = '';
    this.realCardExpYear = '';
    this.realCardCvv = '';
    this.realCardAddress = '';
    this.realCardCity = '';
    this.realCardState = '';
    this.realCardZip = '';
    this.realCardError = null;
  }
  addCard(): void {
    if (!this.canAddCard) return;
    const added = this.payments.add({ brand: this.newCardBrand, last4: this.newCardLast4, makeDefault: false });
    this.selectedPaymentId = added.id;
    this.toasts.success('Card added.');
    this.cancelAddCard();
  }

  /** Real-card form validity — full card number (13-19 digits), MM/YYYY, CVV,
   *  and billing address (the server requires one on file to request a token). */
  get canAddRealCard(): boolean {
    const digits = this.realCardNumber.replace(/\s/g, '');
    return (
      digits.length >= 13 &&
      digits.length <= 19 &&
      /^\d{2}$/.test(this.realCardExpMonth) &&
      /^\d{4}$/.test(this.realCardExpYear) &&
      /^\d{3,4}$/.test(this.realCardCvv) &&
      !!this.realCardAddress.trim() &&
      !!this.realCardCity.trim() &&
      !!this.realCardState.trim() &&
      /^\d{5}$/.test(this.realCardZip)
    );
  }

  async addRealCard(): Promise<void> {
    if (!this.canAddRealCard || !this.user?.id || this.savingRealCard) return;
    this.savingRealCard = true;
    this.realCardError = null;
    try {
      const added = await this.payments.addRealCard({
        userId: this.user.id,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        address: this.realCardAddress.trim(),
        city: this.realCardCity.trim(),
        state: this.realCardState.trim(),
        zipcode: this.realCardZip,
        cardNumber: this.realCardNumber,
        expMonth: this.realCardExpMonth,
        expYear: this.realCardExpYear,
        cvv: this.realCardCvv,
      });
      this.selectedPaymentId = added.id;
      this.toasts.success('Card added.');
      this.cancelAddCard();
    } catch (err) {
      this.realCardError = 'Could not save that card — check the number and try again.';
      console.error('[booking-review] addRealCard failed', err);
    } finally {
      this.savingRealCard = false;
    }
  }

  /** Total nights, integer ≥ 0. */
  get nights(): number {
    if (!this.startDate || !this.endDate) return 0;
    return getNights(this.startDate, this.endDate, 0);
  }

  get subtotal(): number {
    if (!this.listing || !this.startDate || !this.endDate || this.nights === 0) {
      return (this.listing?.price || 0) * this.nights;
    }
    const prices = this.availability.effectivePricesForRange(
      this.listing.id,
      isoKey(this.startDate),
      isoKey(this.endDate),
      this.listing.price,
    );
    let sum = 0;
    for (const p of Object.values(prices)) sum += p;
    return sum;
  }

  /** Min/max real nightly price across the selected range — drives the per-night
   *  label, which shows a single rate when every night matches and a range when
   *  the real calendar prices vary across the stay. Same no-dates-yet fallback as
   *  `subtotal`. */
  get nightlyPriceRange(): { min: number; max: number } {
    if (!this.listing || !this.startDate || !this.endDate || this.nights === 0) {
      return { min: this.listing?.price || 0, max: this.listing?.price || 0 };
    }
    const prices = Object.values(this.availability.effectivePricesForRange(
      this.listing.id,
      isoKey(this.startDate),
      isoKey(this.endDate),
      this.listing.price,
    ));
    if (prices.length === 0) return { min: this.listing.price, max: this.listing.price };
    return { min: Math.min(...prices), max: Math.max(...prices) };
  }

  /** Combined tax across the selected range — sum of all of the host's configured
   *  tax rates, folded into one line. 0 when the host has no tax configured or no
   *  dates are picked yet. Sourced from the same real per-night calendar data as
   *  `subtotal` (see `PriceCalculationService.calendarDates`), kept as a separate
   *  figure rather than folded into subtotal so it can render as its own line item. */
  get taxTotal(): number {
    if (!this.listing || !this.startDate || !this.endDate || this.nights === 0) return 0;
    return this.availability.effectiveTaxForRange(
      this.listing.id,
      isoKey(this.startDate),
      isoKey(this.endDate),
    );
  }

  /** Mock weekly discount: 10% off when stay >= 7 nights. */
  get weeklyDiscount(): number {
    if (this.nights >= 7) return Math.round(this.subtotal * 0.1);
    return 0;
  }

  /** AddOn objects the user picked, in the order they appear on the listing. */
  get selectedAddOns(): IAddOn[] {
    if (!this.detail) return [];
    return this.detail.addOns.filter((a) => this.selectedAddOnIds.has(a.id));
  }

  /** Per-line snapshot ({ ...AddOn, quantity, amount }) using the current multipliers. */
  get addOnSnapshots(): IBookingAddOn[] {
    return this.selectedAddOns.map((a) => ({
      id: a.id,
      label: a.label,
      unit: a.unit,
      unitPrice: a.price,
      quantity: this.addOnQty(a.id),
      amount: this.addOnAmount(a),
      icon: a.icon,
      photo: a.photo,
      realExtraId: a.realExtraId,
    }));
  }

  /** Quantity for an add-on; 1 by default. */
  addOnQty(id: string): number {
    return this.addOnQuantities.get(id) ?? 1;
  }

  /** Per-line total used by both totals and inline display. */
  addOnLineTotal(a: IAddOn): number {
    return this.addOnAmount(a);
  }

  /** Shares `addOnLineAmount` with BookingStateService. These were two separate
   *  copies of the same four-way calculation, so the listing page and this page
   *  could quote different figures for one selection with nothing to catch it. */
  private addOnAmount(a: IAddOn): number {
    return addOnLineAmount(a, { nights: this.nights, guests: this.guests, quantity: this.addOnQty(a.id) });
  }

  get addOnsTotal(): number {
    return this.selectedAddOns.reduce((sum, a) => sum + this.addOnAmount(a), 0);
  }

  toggleAddOn(id: string): void {
    const next = new Set(this.selectedAddOnIds);
    if (next.has(id)) {
      next.delete(id);
      this.addOnQuantities.delete(id);
    } else {
      next.add(id);
      this.addOnQuantities.set(id, 1);
    }
    this.selectedAddOnIds = next;
  }

  /** Upper bound for an add-on's stepper: the host's cap, else 99. */
  addOnMax(a: IAddOn): number {
    return addOnMaxQuantity(a);
  }

  /** Step a selected add-on's quantity, clamped to [1, host cap]. */
  adjustAddOnQty(id: string, delta: number): void {
    const a = this.selectedAddOns.find((x) => x.id === id);
    if (!a) return;
    const next = Math.max(1, Math.min(addOnMaxQuantity(a), this.addOnQty(id) + delta));
    this.addOnQuantities = new Map(this.addOnQuantities).set(id, next);
  }

  isAddOnSelected(id: string): boolean {
    return this.selectedAddOnIds.has(id);
  }

  /** Charged at booking; refunded as CurbNTurf Cash when guest leaves a qualifying review. */
  get feedbackIncentive(): number {
    return computeFeedbackIncentive(this.nights);
  }

  get promoDiscount(): number {
    return this.promoApplied?.amount || 0;
  }

  /** Pre-credit total used to cap the credit redemption. Service fee + feedback
   * incentive are not added on top — they're already inside subtotal. Real host-configured
   * tax is NOT inside subtotal (see `taxTotal`) — it's added on explicitly here so it's
   * still part of the charged/credited total, while rendering as its own line item. */
  get totalBeforeCredit(): number {
    return Math.max(
      0,
      this.subtotal - this.weeklyDiscount + this.addOnsTotal + this.taxTotal - this.promoDiscount,
    );
  }

  /** User toggle — save credit for later vs. apply it to this booking (defaults on). */
  useCredit = true;

  /** Credit actually applied — capped at the pre-credit total. Honors the useCredit toggle.
   *  Real listings never deduct client-side: the server auto-applies CurbNTurf Cash to the
   *  actual card charge on its own (see `isRealListing`'s doc comment) — this total stays
   *  the full pre-cash amount so it doesn't drift from what the server actually computes. */
  get creditApplied(): number {
    if (this.isRealListing || !this.useCredit) return 0;
    return Math.min(this.availableCredit, this.totalBeforeCredit);
  }

  get total(): number {
    return Math.max(0, this.totalBeforeCredit - this.creditApplied);
  }

  get hasValidState(): boolean {
    return !!(this.listing && this.detail && this.nights > 0);
  }

  /** Rig completeness — required to book any stay. */
  get isMyRvComplete(): boolean {
    return !!this.myRv && isMyRvComplete(this.myRv);
  }
  get rigMissingLabel(): string {
    const missing = this.myRv
      ? myRvMissingFields(this.myRv)
      : ['RV type', 'length', 'height', 'width', 'license plate'];
    if (missing.length === 0) return '';
    if (missing.length === 1) return missing[0];
    return missing.slice(0, -1).join(', ') + ' and ' + missing[missing.length - 1];
  }

  /** Switch which saved RV this booking is for — sets it active so every
   * RV-derived getter (completeness, photo gate, summary) recomputes. */
  selectRv(id: string): void {
    setActiveRvProfile(this.platformId, id);
    this.selectedRvId = id;
    this.myRv = readMyRv(this.platformId);
  }

  get rvSummary(): string {
    if (!this.myRv || !isMyRvSet(this.myRv)) return 'Not set';
    const type = rvTypeLabel(this.myRv.type);
    let spec = this.myRv.length ? `${type} · ${this.myRv.length} ft` : type;
    // Towables: surface the tow vehicle (and combined length) for the host.
    const tow = this.myRv.towVehicle;
    if (isTowableRv(this.myRv.type) && towVehicleHasData(tow) && tow) {
      const towName = [tow.year, tow.make, tow.model].filter(Boolean).join(' ') || 'tow vehicle';
      spec += ` · towed by ${towName}`;
      if (this.myRv.length && tow.length) {
        spec += ` (~${this.myRv.length + tow.length} ft total)`;
      }
    }
    const name = this.rvProfiles.find((p) => p.syncId === this.selectedRvId)?.name?.trim();
    return name ? `${name} — ${spec}` : spec;
  }

  get datesLabel(): string {
    if (!this.startDate || !this.endDate) return 'Add dates';
    return formatDateRange(this.startDate, this.endDate);
  }

  get checkInLabel(): string {
    return this.startDate ? this.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  }

  get checkOutLabel(): string {
    return this.endDate ? this.endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  }

  /** Free-cancellation date = check-in minus 3 days. */
  get freeCancelDate(): string {
    if (!this.startDate) return '';
    const d = new Date(this.startDate);
    d.setDate(d.getDate() - 3);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  /** Cancellation timeline mirroring /listing/:id (P2.3 / C). Segments
   *  derive dates from the picked check-in + the listing's cancellation
   *  tier — keeping the trust signal coherent across the funnel. */
  get cancellationSegments(): Array<{ label: string; tone: 'good' | 'warn' | 'bad'; dateLabel: string }> {
    const tier = this.detail?.cancellationTier;
    if (!tier) return [];
    const start = this.startDate;
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const shift = (days: number): string => {
      if (!start) return '';
      const d = new Date(start);
      d.setDate(d.getDate() - days);
      return fmt(d);
    };
    if (tier === 'exclusive') {
      return [{ label: 'Non-refundable from booking', tone: 'bad', dateLabel: '' }];
    }
    if (tier === 'easy-goin') {
      return [
        { label: 'Full refund', tone: 'good', dateLabel: shift(1) || '1 day before check-in' },
        { label: 'No refund', tone: 'bad', dateLabel: '' },
      ];
    }
    if (tier === 'moderate') {
      return [
        { label: 'Full refund', tone: 'good', dateLabel: shift(3) || '3 days before check-in' },
        { label: 'No refund', tone: 'bad', dateLabel: '' },
      ];
    }
    // 'strict'
    return [
      { label: 'Full refund', tone: 'good', dateLabel: shift(7) || '7 days before check-in' },
      { label: '50% refund', tone: 'warn', dateLabel: shift(2) || '2 days before check-in' },
      { label: 'No refund', tone: 'bad', dateLabel: '' },
    ];
  }

  /** "You'll be charged" date = check-in. */
  get chargeDate(): string {
    if (!this.startDate) return 'check-in';
    return this.startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  /** Field-level validation. P58/D — toast on transition from valid →
   *  invalid only, so it fires once per error event without nagging on
   *  every blur. The inline `fieldErrors` text remains the primary
   *  feedback; the toast adds tone parity with /search. */
  validateEmail(): void {
    this.emailTouched = true;
    const wasInvalid = !!this.fieldErrors.email;
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.contactEmail.trim());
    this.fieldErrors.email = ok ? undefined : 'Enter a valid email address.';
    if (!ok && !wasInvalid) this.toasts.info("Email isn't in the right shape.");
  }

  validatePhone(): void {
    this.phoneTouched = true;
    if (!this.contactPhone.trim()) {
      this.fieldErrors.phone = undefined;
      return;
    }
    const wasInvalid = !!this.fieldErrors.phone;
    const digits = this.contactPhone.replace(/\D/g, '');
    const ok = digits.length >= 10;
    this.fieldErrors.phone = ok ? undefined : 'Phone number looks too short.';
    if (!ok && !wasInvalid) this.toasts.info('Phone number needs 10 digits.');
  }

  /** Computed validity for the Confirm CTA. */
  get isFormValid(): boolean {
    if (!this.hasValidState || !this.user) return false;
    if (!this.contactEmail || this.fieldErrors.email) return false;
    if (this.fieldErrors.phone) return false;
    if (!this.acceptedTerms) return false;
    if (!this.isMyRvComplete) return false;
    // Real listings need a real, still-active saved card before the server
    // will authorize the reservation's payment — mock listings never hit
    // this (see the matching runtime guard in onConfirm()).
    if (this.isRealListing && !this.hasValidRealCard) return false;
    return true;
  }

  /** ============ Edit-in-place popovers ============ */
  toggleEditor(which: 'dates' | 'guests'): void {
    this.openEditor = this.openEditor === which ? null : which;
  }
  closeEditor(): void {
    this.openEditor = null;
  }

  /** Used by mat-calendar to gray out blackout/other-guest-reserved dates. */
  dateFilter = (d: Date | null): boolean => {
    if (!d) return false;
    return !this.unavailableSet.has(isoKey(d));
  };

  /** Earliest date the calendar allows picking — reserveDaysInAdvance out from today.
   *  Falls back to today while the listing's bookingBounds() hasn't resolved yet. */
  get minDate(): Date {
    const bounds = this.listing ? this.availability.bookingBounds(this.listing.id) : undefined;
    return (bounds ? parseIsoLocal(bounds.minIso) : null) ?? new Date();
  }

  /** Latest date the calendar allows picking — bookingWindow out from today. Falls back
   *  to a generous default while bookingBounds() hasn't resolved yet. */
  get maxDate(): Date {
    const bounds = this.listing ? this.availability.bookingBounds(this.listing.id) : undefined;
    return (bounds ? parseIsoLocal(bounds.maxIso) : null) ?? new Date(Date.now() + 365 * 86_400_000);
  }

  onDateSelected(date: Date | null): void {
    if (!date) return;
    if (
      !this.selectedDateRange ||
      !this.selectedDateRange.start ||
      (this.selectedDateRange.start && this.selectedDateRange.end)
    ) {
      this.selectedDateRange = new DateRange(date, null);
    } else if (date < this.selectedDateRange.start) {
      this.selectedDateRange = new DateRange(date, null);
    } else {
      this.selectedDateRange = new DateRange(this.selectedDateRange.start, date);
      this.startDate = this.selectedDateRange.start;
      this.endDate = this.selectedDateRange.end;
      setTimeout(() => this.closeEditor(), 200);
    }
  }

  adjustGuests(delta: number): void {
    const max = this.detail?.maxGuests || 8;
    this.guests = Math.max(1, Math.min(max, this.guests + delta));
  }

  /** ============ Promo ============ */
  applyPromo(): void {
    const code = this.promoCode.trim().toUpperCase();
    if (!code) return;
    if (code === 'WELCOME10') {
      this.promoApplied = { code, amount: 25 };
      this.toasts.success('Promo applied — $25 off');
    } else if (code === 'TURF20') {
      this.promoApplied = { code, amount: 50 };
      this.toasts.success('Promo applied — $50 off');
    } else {
      this.toasts.error('Invalid promo code');
      this.promoApplied = null;
    }
    this.promoCode = '';
  }

  removePromo(): void {
    this.promoApplied = null;
    this.toasts.info('Promo removed');
  }

  /** ============ Confirm ============ */
  onConfirm(event: Event): void {
    event.preventDefault();
    // Re-entry guard — Angular CD disables the button after the next tick, but a fast
    // double-click can fire before that, and screen readers / a11y tools can auto-fire.
    if (this.submitting) return;
    this.submitting = true;
    this.error = null;
    this.validateEmail();
    this.validatePhone();
    // Re-read myRv at click time — the user may have edited it in another tab.
    this.myRv = readMyRv(this.platformId);
    if (!this.hasValidState || !this.listing || !this.detail || !this.user) {
      this.error = 'Missing booking details. Start over from the property.';
      this.submitting = false;
      return;
    }
    if (!this.acceptedTerms) {
      this.error = 'Please accept the booking terms to continue.';
      this.submitting = false;
      return;
    }
    if (this.fieldErrors.email || !this.contactEmail) {
      this.error = 'A valid contact email is required.';
      this.submitting = false;
      return;
    }
    if (!this.isMyRvComplete) {
      this.error = `Add your ${this.rigMissingLabel} before booking.`;
      this.submitting = false;
      return;
    }
    // Photo gate — request-to-book listings require rig + plate photos. The booking widget
    // gates this on the listing page, but a deep-link can land directly here, so re-check.
    if (this.listing.reservationProcess !== 'instant' && !hasMyRvPhotos(this.myRv)) {
      this.error = 'Add your rig and license-plate photos in your profile before completing this booking.';
      this.submitting = false;
      return;
    }
    // Date availability — listing-side (unavailable dates) and user-side (self double-book).
    if (!this.isWithinBookingWindow(this.startDate!, this.endDate!)) {
      this.error = `This listing needs more advance notice for these dates — pick a check-in on or after ${this.minDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.`;
      this.submitting = false;
      return;
    }
    const range = { start: this.startDate!.toISOString(), end: this.endDate!.toISOString() };
    if (!this.isDateRangeAvailable(this.detail, this.startDate!, this.endDate!)) {
      this.error = 'Those dates are not available. Please pick a different range.';
      this.submitting = false;
      return;
    }
    if (this.booking.hasUserDateConflict(this.user.email, this.listing.id, range)) {
      this.error = 'You already have a booking at this property during those dates.';
      this.submitting = false;
      return;
    }
    // todo: add back when we have identity verification
    // Gate first booking on identity verification.
    // if (!this.user.verified) {
    //   this.idVerifyOpen = true;
    //   this.submitting = false; // re-enables the button while the modal is up
    //   return;
    // }
    // Real listings need a real, still-active saved card before the server
    // will authorize the reservation's payment — mock listings never hit
    // this. Distinguish "no card at all" from "we double-checked and the
    // server no longer recognizes it" (hydrateRealCard()) so the error
    // points the guest at the right fix.
    if (this.isRealListing && !this.paymentMethods.some((m) => m.isReal)) {
      this.error = 'Add a real card above before booking a live property.';
      this.submitting = false;
      return;
    }
    if (this.isRealListing && !this.hasValidRealCard) {
      this.error = 'Your saved card is no longer valid — please add a new one.';
      this.submitting = false;
      return;
    }
    void this.finalizeBooking();
  }

  /** Guards the deep-link/stale-review-URL path where finishInit() read an
   *  out-of-window date straight from query params — isDateRangeAvailable()
   *  only checks blackout dates, not the reserveDaysInAdvance/bookingWindow bound.
   *  Only enforced once bookingBounds() has resolved; minDate/maxDate fall back to
   *  permissive defaults while that fetch is in flight (see their own doc comments),
   *  and blocking against those fallbacks would wrongly reject valid dates. */
  private isWithinBookingWindow(start: Date, end: Date): boolean {
    const bounds = this.listing ? this.availability.bookingBounds(this.listing.id) : undefined;
    if (!bounds) return true;
    return isoKey(start) >= bounds.minIso && isoKey(end) <= bounds.maxIso;
  }

  /**
   * Check every day in [start, end) against the listing's unavailable-date set.
   *
   * `unavailableDates` is optional (F12): a detail built by the placeholder stub
   * does not know the blackouts, and an empty array there would have read as
   * "nothing is booked". This component builds `detail` from
   * `toListingDetailFromEntity`, so the value is real here — the guard exists so
   * that if this is ever handed a stub-built detail it refuses the booking rather
   * than waving it through. Failing closed is the only safe direction when the
   * failure mode is a double booking.
   */
  private isDateRangeAvailable(detail: IListingDetail, start: Date, end: Date): boolean {
    if (!detail.unavailableDates) return false;
    const unavailable = new Set(detail.unavailableDates);
    const day = new Date(start);
    day.setHours(0, 0, 0, 0);
    const endDay = new Date(end);
    endDay.setHours(0, 0, 0, 0);
    while (day < endDay) {
      const iso = day.toISOString().slice(0, 10);
      if (unavailable.has(iso)) return false;
      day.setDate(day.getDate() + 1);
    }
    return true;
  }

  onVerified(): void {
    this.idVerifyOpen = false;
    this.user = this.auth.currentUser;
    void this.finalizeBooking();
  }

  private async finalizeBooking(): Promise<void> {
    if (!this.listing || !this.detail || !this.user) return;
    this.submitting = true;
    try {
      const created = await this.booking.createBooking({
        userEmail: this.user.email,
        listingId: this.listing.id,
        // The site to book — resolved by resolveSiteId()/the `siteId` query param.
        // `IBooking.realSiteId` doubles as create-input here; `createBooking()` uses it
        // to route to the real `POST /reservation`, and `toBookingView()` overwrites it
        // with the authoritative value from the created reservation afterward.
        realSiteId: this.siteId ?? undefined,
        listingTitle: this.listing.title,
        listingLocation: this.listing.location,
        listingPhoto: this.detail.photos[0] || '',
        hostName: this.detail.host.name,
        hostAddress: undefined,
        lat: this.listing.coordinates.lat,
        lng: this.listing.coordinates.lon,
        dates: { start: this.startDate!.toISOString(), end: this.endDate!.toISOString() },
        nights: this.nights,
        guests: this.guests,
        rvSummary: this.rvSummary,
        // Average real (post-fee) nightly rate actually paid, not the raw pre-fee
        // listing.price — kept consistent with subtotal/total below, which already
        // reflect the real per-night calendar prices.
        pricePerNight: this.nights > 0 ? this.subtotal / this.nights : this.listing.price,
        subtotal: this.subtotal,
        cleaningFee: 0,
        serviceFee: 0,
        feedbackIncentive: this.feedbackIncentive,
        total: this.total,
        instantBook: this.listing.reservationProcess === 'instant',
        status: this.listing.reservationProcess === 'instant' ? 'confirmed' : 'pending',
        contact: { email: this.contactEmail, phone: this.contactPhone || undefined },
        requestMessage:
          this.listing.reservationProcess !== 'instant' && this.noteToHost.trim() ? this.noteToHost.trim() : undefined,
        addOns: this.addOnSnapshots.length > 0 ? this.addOnSnapshots : undefined,
        addOnsTotal: this.addOnsTotal || undefined,
        creditApplied: this.creditApplied > 0 ? this.creditApplied : undefined,
      });
      this.submitting = false;
      this.toasts.success(
        this.listing.reservationProcess === 'instant'
          ? 'Booking confirmed!'
          : 'Request sent — host has 24h to respond.',
      );
      this.router.navigate(['/booking/confirm', created.id]);
    } catch (err) {
      this.submitting = false;
      this.error = this.extractBookingError(err);
      this.toasts.error(this.error);
      console.error('[booking-review] createBooking failed', err);
    }
  }

  private extractBookingError(err: unknown): string {
    if (err instanceof HttpErrorResponse) return stringFromError(err);
    if (err instanceof Error && err.message) return err.message;
    return 'Could not complete the booking — the payment may have been declined. Please try again.';
  }

  private parseIso(s: string): Date | null {
    return parseIsoLocal(s);
  }
}
